/**
 * UTM Tracking Utility — Emad Platform
 *
 * Captures utm_* + referrer params from the URL and persists them in
 * sessionStorage so they survive navigation between public pages
 * (Landing → Opportunities → Register).
 *
 * PII policy (per PRD Phase 19):
 *   - We ONLY capture: utm_source, utm_medium, utm_campaign,
 *     utm_term, utm_content, gclid, fbclid, and referrer.
 *   - NO email, name, phone, or any user identifier is stored here.
 *   - Data is scoped to sessionStorage (cleared on tab close).
 *
 * Usage:
 *   import { captureUtm, getUtm, clearUtm, useUtmCapture } from "@/lib/utm";
 *
 *   // In Landing (or any public entry point):
 *   useUtmCapture();
 *
 *   // On successful registration:
 *   const utm = getUtm();
 *   posthog?.capture?.("emad_signup", { ...utm, role: user.role });
 *   clearUtm();
 */

import { useEffect } from "react";
import { trackPixel } from "@/lib/analytics";

const STORAGE_KEY = "emad_utm";
const ALLOWED_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
];

export function captureUtm() {
  try {
    const params = new URLSearchParams(window.location.search);
    const found = {};
    ALLOWED_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) found[k] = v.slice(0, 120); // cap length
    });

    // Only refresh storage when the URL brings NEW UTM data, so a later page
    // visit without params does not wipe the original attribution.
    if (Object.keys(found).length === 0) return getUtm();

    const referrer =
      document.referrer && !document.referrer.startsWith(window.location.origin)
        ? document.referrer.slice(0, 200)
        : "";

    const payload = {
      ...found,
      referrer,
      landing_path: window.location.pathname.slice(0, 120),
      captured_at: new Date().toISOString(),
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    return payload;
  } catch {
    return {};
  }
}

export function getUtm() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function clearUtm() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

/**
 * React hook — call once at the top of a public page (Landing, Opportunities)
 * to persist inbound UTM params without changing the URL for the user.
 */
export function useUtmCapture() {
  useEffect(() => {
    captureUtm();
  }, []);
}

/**
 * Emit an analytics event (posthog) with the current UTM attribution.
 * Safe if posthog is not loaded — becomes a no-op.
 * We NEVER include PII: only the role and non-identifying UTM keys.
 */
export function trackSignup(role) {
  try {
    const utm = getUtm();
    const payload = { role, ...utm };
    const pixelAttribution = {};
    ALLOWED_KEYS.forEach((key) => {
      if (utm[key]) pixelAttribution[key] = utm[key];
    });
    if (typeof window !== "undefined" && window.posthog?.capture) {
      window.posthog.capture("emad_signup", payload);
    }
    // GA4 — high-value conversion event, forwarded to both properties.
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "sign_up", { method: role || "email", ...utm });
      window.gtag("event", "emad_signup", payload);
      window.gtag("set", "user_properties", { app_role: role });
    }
    // Meta Pixel — CompleteRegistration standard conversion.
    // Fires ONLY here (after registration API resolved), never on submit click.
    trackPixel("CompleteRegistration", {
      content_name: "emad_" + (role || "user") + "_signup",
      content_category: "registration",
      status: true,
      ...pixelAttribution,
    });
    // Also expose on window for debugging in staging
    if (typeof window !== "undefined") {
      window.__emad_last_signup__ = payload;
    }
  } catch {
    /* noop */
  }
}

/**
 * Append currently-active URL UTM params (from `window.location.search`) to a
 * relative internal href. This keeps attribution visible on the URL for the
 * remainder of the user's session — analytics tools that read from `location`
 * (Google Analytics, Meta Pixel, PostHog auto-capture) continue to work
 * across internal navigation.
 *
 * withUtm("/opportunities")                 → "/opportunities?utm_source=..."
 * withUtm("/register?role=mentor")          → "/register?role=mentor&utm_source=..."
 */
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid"];

export function withUtm(to) {
  try {
    if (!to || typeof to !== "string") return to;
    if (/^(https?:|mailto:|tel:|#)/i.test(to)) return to;
    const current = new URLSearchParams(window.location.search);
    const carry = UTM_KEYS.filter((k) => current.get(k));
    if (carry.length === 0) return to;

    const [path, existingQs = ""] = to.split("?");
    const next = new URLSearchParams(existingQs);
    carry.forEach((k) => { if (!next.get(k)) next.set(k, current.get(k)); });
    const qs = next.toString();
    return qs ? `${path}?${qs}` : path;
  } catch {
    return to;
  }
}
